import { FieldValues } from "react-hook-form";
import { supabase } from "../supabaseClient";
import { Feed, Feeds } from "../types/api/Feed";
import { MEDIA_SIZE, getMediaPathWithFilename } from "./files";
import { setup_notifications } from "./setup_notification";
import { SelectedFile } from "../types/PostSelection";
import { Post } from "../types/api/Post";
import { getSelectedMediaIds } from "./helper/getSelectedMediaIds";
import { hasCompleteImageDerivatives } from "../shared/imageDerivatives";
import {
  getSelectedFileKey,
  resolvePostMediaSelections,
} from "./helper/resolvePostMediaSelections";
import { Database } from "../types/supabase";

type PostMediaInsert = Database["public"]["Tables"]["posts_media"]["Insert"];

export async function schedule_update(
  feeds: Feeds,
  values: FieldValues,
  selected: SelectedFile[],
  existing_post: Post
) {
  const { allFiles: imgs, feedSelections } = resolvePostMediaSelections(
    feeds,
    values,
    selected
  );

  for (const img of imgs) {
    if (
      !isVideoSelection(img) &&
      !hasCompleteImageDerivatives(img.imageDerivatives)
    ) {
      throw new Error(`Image derivatives are unavailable for ${img.name}`);
    }
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new Error("User not authenticated");
  }

  const media: PostMediaInsert[] = [];

  for (let index = 0; index < imgs.length; index++) {
    const img = imgs[index];
    const isVideo = isVideoSelection(img);
    const filename = isVideo
      ? img.name.replace(".video-thumbnail.jpg", "")
      : img.name;
    let mimetype = img.mine_type;

    if (isVideo) {
      const { data: videoDetails, error: videoError } = await supabase.storage
        .from("content")
        .list(`${session.user.id}/media/video`, { search: filename });

      if (videoError) throw videoError;
      if (!videoDetails?.[0]?.metadata?.mimetype) {
        throw new Error(`Video metadata is unavailable for ${filename}`);
      }
      mimetype = videoDetails[0].metadata.mimetype;
    }

    const imageDerivatives = img.imageDerivatives;
    if (!isVideo) {
      mimetype = imageDerivatives!.derivatives["3600px"]!.mediaType;
    }

    media.push({
      post_id: existing_post.id,
      post: existing_post.id,
      title: img.name,
      alt_text: img.altText || null,
      src_thumbnail: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.thumbnail, img.name)
        : imageDerivatives!.derivatives.thumbnail!.path,
      src_960: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.derivatives["960px"]!.path,
      src_2400: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.derivatives["2400px"]!.path,
      src_3600: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.derivatives["3600px"]!.path,
      src_original: isVideo
        ? await getMediaPathWithFilename(MEDIA_SIZE.video, filename)
        : imageDerivatives!.originalPath,
      mine_type: mimetype,
      user_id: session.user.id,
      order: index,
    });
  }

  const { data: post, error: postError } = await supabase
    .from("posts")
    .update({
      type: values.default.type,
      publish_at: new Date(values.date).toUTCString(),
      disable_auto_publish: !!values.disable_auto_publish,
      user_id: session.user.id,
    })
    .eq("id", existing_post.id)
    .select()
    .order("id", { ascending: true })
    .limit(1)
    .single();

  if (postError) throw postError;

  const { error: deleteMediaError } = await supabase
    .from("posts_media")
    .delete()
    .eq("post", existing_post.id);
  if (deleteMediaError) throw deleteMediaError;

  const { error: deleteFeedsError } = await supabase
    .from("posts_for_feeds")
    .delete()
    .eq("post", existing_post.id);
  if (deleteFeedsError) throw deleteFeedsError;

  const { data: postMedia, error: postMediaError } = await supabase
    .from("posts_media")
    .insert(media)
    .select("id, src_original");

  if (postMediaError) throw postMediaError;

  const enhancedImgs =
    postMedia
      ?.map((pm) => {
        const inputIndex = media.findIndex(
          (item) => item.src_original === pm.src_original
        );
        if (inputIndex === -1) return null;

        return {
          postMediaId: pm.id,
          selectionKey: getSelectedFileKey(imgs[inputIndex]),
        };
      })
      .filter(
        (
          item
        ): item is {
          postMediaId: number;
          selectionKey: string;
        } => !!item
      ) ?? [];

  const postsForFeeds = feedSelections.map(({ feed, files }) => {
    const existingPostForFeed = existing_post.posts_for_feeds?.find(
      (pff) => (pff.feed as Feed)._id === feed._id
    );

    return {
      post_id: post.id,
      post: post.id,
      feed_id: feed.id,
      feed: feed.id,
      content: values[feed.slug!].post_content,
      auto_publish: !!values[feed.slug!].auto_publish,
      type: values[feed.slug!].type,
      location: values[feed.slug!].location,
      hashtags: values[feed.slug!].hashtags,
      positions: values[feed.slug!].positions || {},
      selected_saved_descriptions:
        values[feed.slug!].selectedSavedDescriptions || [],
      user_id: session.user.id,
      selectedFiles: getSelectedMediaIds(enhancedImgs, files),
      published: existingPostForFeed?.published ?? false,
      live_post_id: existingPostForFeed?.live_post_id ?? null,
    };
  });

  const { error: feedsError } = await supabase
    .from("posts_for_feeds")
    .insert(postsForFeeds);
  if (feedsError) throw feedsError;

  await setup_notifications("update", post);

  return {
    error: null,
  };
}

function isVideoSelection(file: SelectedFile) {
  return (
    file.mine_type.startsWith("video/") ||
    file.name.endsWith(".video-thumbnail.jpg")
  );
}
